/**
 * Is this MP4 streamable, or will the browser download all of it before the
 * first frame?
 *
 * An MP4 is a flat list of boxes. Two matter: `moov` (the index — durations,
 * frame offsets, codec config) and `mdat` (the actual video bytes). A browser
 * cannot render anything until it has `moov`, so if the recorder wrote it
 * AFTER `mdat` — which most screen recorders do, because the index isn't final
 * until recording stops — the whole file must arrive first. "Faststart" just
 * means `moov` was moved to the front afterwards.
 *
 * The symptom is not an error. The player renders, the controls appear, and
 * pressing play does nothing visible while the browser silently downloads. On a
 * 97 MB clip that reads as broken.
 *
 * Receipt: 2026-08-10 — the first real demo video uploaded to staging was 97 MB
 * of 2850×1504 at ~18 Mbps with `moov` at the end. Re-encoding to 1280-wide
 * with `+faststart` produced the same 45 seconds in 1.19 MB, playing instantly.
 *
 * Reads only box HEADERS (8 bytes each) via `File.slice`, walking the size
 * fields — so a 100 MB file costs a few hundred bytes of reads, not a full
 * load into memory.
 */

/** What a scan concluded about a candidate video file. */
export interface Mp4StreamCheck {
  /** True when `moov` precedes `mdat` — the browser can start immediately. */
  faststart: boolean;
  /** False when the bytes aren't a recognisable MP4 (e.g. a WebM, or garbage). */
  parsed: boolean;
}

/** Max top-level boxes to walk before giving up — a sane MP4 has a handful. */
const MAX_BOXES = 24;

/** How far in we're willing to look before treating the file as unparseable. */
const MAX_SCAN_BYTES = 64 * 1024 * 1024;

const readHeader = async (file: File, offset: number): Promise<{ size: number; type: string } | null> => {
  if (offset + 8 > file.size) return null;
  const buf = await file.slice(offset, offset + 16).arrayBuffer();
  const view = new DataView(buf);
  if (buf.byteLength < 8) return null;

  let size = view.getUint32(0);
  // A box type is exactly four ASCII bytes, so read them directly rather than
  // via TextDecoder — which jsdom does not provide, and which would make this
  // untestable for no benefit.
  const type = String.fromCharCode(view.getUint8(4), view.getUint8(5), view.getUint8(6), view.getUint8(7));

  // size === 1 means the real 64-bit size follows the type field.
  if (size === 1) {
    if (buf.byteLength < 16) return null;
    const hi = view.getUint32(8);
    const lo = view.getUint32(12);
    size = hi * 2 ** 32 + lo;
  }
  // size === 0 means "to end of file".
  if (size === 0) size = file.size - offset;

  return { size, type };
};

/**
 * Walk the top-level boxes and report which of `moov` / `mdat` comes first.
 *
 * Returns `parsed: false` rather than guessing when the file isn't an MP4 we
 * recognise — the caller must not warn on a WebM, which has no such problem.
 */
export async function checkMp4Faststart(file: File): Promise<Mp4StreamCheck> {
  let offset = 0;

  for (let i = 0; i < MAX_BOXES && offset < MAX_SCAN_BYTES; i += 1) {
    const header = await readHeader(file, offset);
    if (!header) break;

    // A sane box is at least its own header; anything else means we've lost
    // sync and should not draw a conclusion from it.
    if (header.size < 8) break;

    if (header.type === 'moov') return { faststart: true, parsed: true };
    if (header.type === 'mdat') return { faststart: false, parsed: true };

    offset += header.size;
  }

  return { faststart: false, parsed: false };
}
